import { AlertCircle, Mail, Phone } from 'lucide-react';

import { ThumbCheck } from '@/assets/icons/ThumbCheck.tsx';
import AppointmentCard from '@/components/appointment/AppointmentCard';
import ClinicianInfoCard from '@/components/appointment/ClinicianInfoCard';
import Hr from '@/components/common/Hr.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { useRunOnce } from '@/hooks/use-run-once.ts';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store.ts';
import { endTrackingSession } from '@/lib/analytics';
import {
  SUPPORT_EMAIL,
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_E164,
  TELEHEALTH_CONTACT_TYPE,
} from '@/lib/constants.ts';
import { ORDER_ERROR_CODES, getApiErrorCode } from '@/lib/errors.ts';
import { cn } from '@/lib/utils.ts';
import type { Order } from '@/types/order.type.ts';
import { useOrderStore } from '@/views/hooks/use-order-store.ts';
import NoAppointmentCard from '@/views/order/components/NoAppointmentCard.tsx';

import { useCreateOrder } from '../hooks/create-order-context.tsx';
import PreparationNotes from './components/PreparationNotes.tsx';

export function DisplayOrder() {
  const { mutation } = useCreateOrder();
  const { order: persistedOrder } = useOrderStore();

  const isExistingOrderError =
    getApiErrorCode(mutation.error) === ORDER_ERROR_CODES.PATIENT_HAS_EXISTING_ORDER;

  return (
    <section>
      {persistedOrder && <OrderDisplay order={persistedOrder} />}
      {!persistedOrder && mutation.status === 'pending' && <OrderSkeleton />}
      {!persistedOrder &&
        mutation.status === 'error' &&
        (isExistingOrderError ? <OrderExistingPatientError /> : <OrderError />)}
      {!persistedOrder && mutation.status === 'idle' && <OrderError />}
    </section>
  );
}

function OrderSkeleton() {
  return (
    <Card className="gap-6">
      <CardHeader>
        <CardTitle className="flex flex-col items-center gap-4">
          <ThumbCheck className={cn('size-15 saturate-0')} />
          <Skeleton className="h-4 w-full" />
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-24 w-full" />
        <div className="flex gap-4">
          <Skeleton className="h-24 w-24" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-[30vw]" />
            <Skeleton className="h-4 w-[25vw]" />
            <Skeleton className="h-4 w-[25vw]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function OrderDisplay({ order }: { order: Order }) {
  const { representativeAuthorizedForMedicalDecisions, schedulingSelectedContactType } =
    useSessionStore();
  const patientPresentNote = representativeAuthorizedForMedicalDecisions === 'no';

  const isPhoneCall = schedulingSelectedContactType === TELEHEALTH_CONTACT_TYPE.PHONE;
  useRunOnce(() => {
    endTrackingSession();
  });

  return (
    <Card className="gap-6">
      <CardHeader>
        <CardTitle className="flex flex-col items-center gap-4">
          <ThumbCheck className={cn('size-15')} />
          <h1>You're all set!</h1>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="flex flex-col justify-center gap-6">
          <p className="mx-8 text-center">
            {order.appointment
              ? 'Your Telehealth appointment has been scheduled.'
              : 'Your order has been submitted'}
          </p>
          <Hr className="my-0" />
        </div>

        {patientPresentNote && (
          <div className="border-orchid-500 space-y-1 border-l-2 pl-4 text-sm">
            <h4 className="text-orchid-600">Important note</h4>
            <p>Patient must be present for Telehealth visit</p>
          </div>
        )}

        {order.appointment ? (
          <>
            <AppointmentCard appointment={order.appointment} isPhoneCall={isPhoneCall} />
            <ClinicianInfoCard clinician={order.appointment.provider} />
          </>
        ) : (
          <NoAppointmentCard />
        )}
        <PreparationNotes />
      </CardContent>
    </Card>
  );
}

function OrderError() {
  const handleGoHome = () => {
    window.location.href = '/0';
  };

  return (
    <Card className="gap-6">
      <CardHeader>
        <CardTitle className="flex flex-col items-center gap-4">
          <AlertCircle className={cn('size-12 text-rose-500')} />
          <h1 className="text-rose-500">Your order cannot be created</h1>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        <p className="text-charcoal-900 mb-6 text-center text-lg">
          We're sorry, but something unexpected happened.
        </p>

        <p className="text-charcoal-700 mb-8 text-center text-base">
          You can try again or start over. If this keeps happening, please call us at{' '}
          <a href={`tel:${SUPPORT_PHONE_E164}`} className="font-semibold underline">
            {SUPPORT_PHONE_DISPLAY}
          </a>
          .
        </p>

        <div className="flex justify-center">
          <Button onClick={handleGoHome}>Start Over</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function OrderExistingPatientError() {
  const handleCallUs = () => {
    window.location.href = `tel:${SUPPORT_PHONE_E164}`;
  };
  const handleEmailUs = () => {
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  };
  return (
    <Card className="gap-6">
      <CardHeader className="lg:justify-items-center lg:text-center">
        <CardTitle className="flex flex-col items-center gap-4 lg:w-full lg:text-center">
          <h1 className="lg:text-center">It looks like you already have an existing request</h1>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        <p className="text-charcoal-900 mb-6 text-lg lg:text-center">
          Want to make changes to your order?
        </p>

        <div className="mt-8 flex w-full justify-center gap-6">
          <Button
            type="button"
            className="min-w-0 flex-1 sm:w-auto sm:flex-none lg:min-w-[144px]"
            onClick={handleCallUs}
          >
            Call us
            <Phone className="size-4" />
          </Button>
          <Button
            type="button"
            className="min-w-0 flex-1 sm:w-auto sm:flex-none lg:min-w-[144px]"
            onClick={handleEmailUs}
          >
            Email us
            <Mail className="size-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
